import type { FuelCardAction } from './actions';
import { FuelCardActionTypes } from './fuelCardActionTypes';

import type { FuelCardVehicle, FuelTransaction } from '@cobliteam/common-types';

import { type FuelCardMatching, type WebData, mapWebData, webDataError, webDataFetching, webDataNotAsked, webDataSuccess } from '@cobliteam/api-lib';

export interface FuelCardState {
  transactionsReportWebData: WebData<void>;
  transactionsWebData: WebData<FuelTransaction[]>;
  vehiclesWebData: WebData<FuelCardVehicle[]>;
  updateVehiclesWebData: WebData<void>;
  fuelCardMatchingsWebData: WebData<FuelCardMatching[]>;
  saveFuelCardMatchingWebData: WebData<void>;
}

export const fuelCardInitialState: FuelCardState = {
  transactionsReportWebData: webDataNotAsked(),
  transactionsWebData: webDataNotAsked(),
  vehiclesWebData: webDataNotAsked(),
  updateVehiclesWebData: webDataNotAsked(),
  fuelCardMatchingsWebData: webDataNotAsked(),
  saveFuelCardMatchingWebData: webDataNotAsked(),
};

export const fuelCardReducer = (state: FuelCardState = fuelCardInitialState, action: FuelCardAction): FuelCardState => {
  switch (action.type) {
    case FuelCardActionTypes.DOWNLOAD_TRANSACTIONS_REPORT_REQUEST:
      return { ...state, transactionsReportWebData: webDataFetching() };

    case FuelCardActionTypes.DOWNLOAD_TRANSACTIONS_REPORT_END:
      return { ...state, transactionsReportWebData: webDataNotAsked() };

    case FuelCardActionTypes.LOAD_FUEL_TRANSACTIONS_REQUEST:
      return { ...state, transactionsWebData: webDataFetching() };

    case FuelCardActionTypes.LOAD_FUEL_TRANSACTIONS_SUCCEEDED:
      return {
        ...state,
        transactionsWebData: webDataSuccess(action.payload),
      };

    case FuelCardActionTypes.LOAD_FUEL_TRANSACTIONS_FAILED:
      return { ...state, transactionsWebData: webDataError() };

    case FuelCardActionTypes.SAVE_FUEL_TRANSACTIONS_REQUEST:
      return { ...state, transactionsWebData: webDataFetching() };

    case FuelCardActionTypes.SAVE_FUEL_TRANSACTIONS_FAILED:
      return { ...state, transactionsWebData: webDataError() };

    case FuelCardActionTypes.LOAD_FUEL_VEHICLES_REQUEST:
      return { ...state, vehiclesWebData: webDataFetching() };

    case FuelCardActionTypes.LOAD_FUEL_VEHICLES_SUCCEEDED:
      return { ...state, vehiclesWebData: webDataSuccess(action.payload) };

    case FuelCardActionTypes.LOAD_FUEL_VEHICLES_FAILED:
      return { ...state, vehiclesWebData: webDataError() };

    case FuelCardActionTypes.UPDATE_FUEL_VEHICLES_REQUEST:
      return { ...state, updateVehiclesWebData: webDataFetching() };

    case FuelCardActionTypes.UPDATE_FUEL_VEHICLES_END:
      return { ...state, updateVehiclesWebData: webDataNotAsked() };

    case FuelCardActionTypes.LOAD_FUEL_CARD_MATCHINGS_REQUEST:
      return { ...state, fuelCardMatchingsWebData: webDataFetching() };

    case FuelCardActionTypes.LOAD_FUEL_CARD_MATCHINGS_SUCCEEDED:
      return {
        ...state,
        fuelCardMatchingsWebData: webDataSuccess(action.payload),
      };

    case FuelCardActionTypes.LOAD_FUEL_CARD_MATCHINGS_FAILED:
      return { ...state, fuelCardMatchingsWebData: webDataError() };

    case FuelCardActionTypes.SAVE_FUEL_CARD_MATCHING_REQUEST:
      return { ...state, saveFuelCardMatchingWebData: webDataFetching() };

    case FuelCardActionTypes.SAVE_FUEL_CARD_MATCHING_SUCCEEDED:
      const { id, licensePlate } = action.payload;
      return {
        ...state,
        saveFuelCardMatchingWebData: webDataSuccess(undefined),
        fuelCardMatchingsWebData: mapWebData((fuelCardMatchings: FuelCardMatching[]) =>
          fuelCardMatchings.map((match) => {
            if (match.id === id) {
              return {
                ...match,
                licensePlate,
              };
            }
            return match;
          })
        )(state.fuelCardMatchingsWebData),
      };

    case FuelCardActionTypes.SAVE_FUEL_CARD_MATCHING_FAILED:
      return { ...state, saveFuelCardMatchingWebData: webDataError() };

    default:
      return state;
  }
};
